<script setup>
import { ref, watch } from "vue";
const Dialog = defineAsyncComponent(() => import("primevue/dialog"));
const WrForm = defineAsyncComponent(() => import("../molecules/WrForm.vue"));

const WrThankYou = defineAsyncComponent(() =>
  import("~/components/molecules/WrThankYou.vue")
);
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
    required: true,
  },
  prefilledContent: {
    required: false,
  },
  page: {
    required: false,
    default: "",
  },
  modalBg: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close"]);
const visible = ref(props.isOpen);
// Local mirror of the prefilledContent prop so we can clear it after a
// successful submit without mutating the (read-only) prop.
const prefilled = ref(props.prefilledContent);

const isSuccess = ref(false);
const formSubmitSuccessFully = () => {
  isSuccess.value = true;
  prefilled.value = [];
};
const onBackToForm = () => {
  isSuccess.value = false;
};

watch(
  () => props.isOpen,
  (newVal) => {
    visible.value = newVal;
    if (newVal) prefilled.value = props.prefilledContent;
  }
);

watch(visible, (newVal) => {
  if (!newVal) {
    emit("close");
  }
});

const handleClose = () => {
  if (isSuccess.value) {
    isSuccess.value = false;
  }
  visible.value = false;
};
</script>

<template>
  <div class="card flex justify-center z-30">
    <Dialog
      aria-label="Contact Form"
      @hide="handleClose"
      v-model:visible="visible"
      :modal="true"
      :dismissableMask="true"
      closeOnEscape
      closable
      unstyled
      class="w-[90vw] md:w-[60vw] rounded-xl"
      :breakpoints="{ '1199px': '70vw', '575px': '90vw' }"
      :pt="{
        root: {
          class:
            'relative dark:bg-[var(--modal-bg,#35095b)] rounded-xl',
          style: {
            '--modal-bg': props.modalBg || undefined,
            /* Darker fill for the inputs so they stand out on the custom panel */
            '--modal-field-bg': props.modalBg
              ? `color-mix(in srgb, ${props.modalBg} 60%, black)`
              : undefined,
          },
        },
        headerActions: {
          class: 'hidden',
        },
        mask: {
          class: 'backdrop-blur-sm',
        },
        content: { class: 'max-h-[80vh] overflow-y-auto' },
      }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-desc"
    >
      <template #header>
        <div class="absolute -top-3 -right-3 z-40">
          <button
            @click="handleClose"
            aria-label="Close dialog"
            class="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <WrSVGIcon
              name="WrCrossIcon"
              class="text-white p-1 bg-[#5f4ca4] rounded-full border-2 size-[30px]"
            />
          </button>
        </div>
      </template>
      <div class="rounded-xl overflow-hidden">
        <h2 id="dialog-title" class="sr-only">Contact Form Dialog</h2>
        <p id="dialog-desc" class="sr-only">
          Fill out the form or review submission status in this modal dialog.
        </p>
        <WrThankYou
          v-if="isSuccess"
          @back-to-form="onBackToForm"
          :layoutChange="true"
          role="status"
          aria-live="polite"
        />
        <WrForm
          v-else
          :layoutChange="true"
          :prefilledContent="prefilled"
          @formsubmitted="formSubmitSuccessFully"
          :page="props.page"
        />
      </div>
    </Dialog>
  </div>
</template>
<style type="css" scoped>
button:focus,
input:focus,
textarea:focus {
  outline: 2px solid #16a286;
  outline-offset: 2px;
}
</style>
